import { useMemo } from "react";
import { Flex, Text } from "@radix-ui/themes";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const SENTIMENTS = ["positive", "neutral", "negative"];

const startOfWeek = (date) => {
  const result = new Date(date);
  const day = result.getUTCDay();
  result.setUTCDate(result.getUTCDate() - ((day + 6) % 7));
  return result.toISOString().slice(0, 10);
};

const monthKey = (date) => date.toISOString().slice(0, 7);
const yearKey = (date) => String(date.getUTCFullYear());

const formatDate = (value, includeYear = false) => {
  if (value.length === 4) return value;
  const date = new Date(`${value}${value.length === 7 ? "-01" : ""}T00:00:00Z`);
  return date.toLocaleDateString("en-GB", {
    day: value.length === 7 ? undefined : "numeric",
    month: "short",
    year: includeYear ? "numeric" : undefined,
    timeZone: "UTC",
  });
};

const prepareTrend = (data) => {
  if (!data?.length) return [];

  const first = new Date(`${data[0].date}T00:00:00Z`);
  const last = new Date(`${data[data.length - 1].date}T00:00:00Z`);
  const spanDays = Math.max(0, (last - first) / 86400000);
  const grouping = spanDays > 730 ? "year" : spanDays > 120 ? "month" : spanDays > 35 ? "week" : "day";
  const buckets = new Map();

  data.forEach((item) => {
    const date = new Date(`${item.date}T00:00:00Z`);
    const key = grouping === "year" ? yearKey(date) : grouping === "month" ? monthKey(date) : grouping === "week" ? startOfWeek(date) : item.date;
    const bucket = buckets.get(key) || { date: key, positive: 0, neutral: 0, negative: 0 };
    SENTIMENTS.forEach((sentiment) => {
      bucket[sentiment] += Number(item[sentiment] || 0);
    });
    buckets.set(key, bucket);
  });

  return Array.from(buckets.values()).map((bucket) => {
    const total = SENTIMENTS.reduce((sum, sentiment) => sum + bucket[sentiment], 0);
    return {
      date: bucket.date,
      label: formatDate(bucket.date, grouping === "month" && spanDays > 365),
      positive: total ? Math.round((bucket.positive / total) * 1000) / 10 : 0,
      neutral: total ? Math.round((bucket.neutral / total) * 1000) / 10 : 0,
      negative: total ? Math.round((bucket.negative / total) * 1000) / 10 : 0,
    };
  });
};

const TrendTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: "var(--color-panel-solid)", border: "1px solid var(--gray-a5)", borderRadius: 10, padding: "10px 12px", boxShadow: "var(--shadow-3)" }}>
      <Text as="div" size="2" weight="bold" mb="2">{label}</Text>
      {payload.map((entry) => (
        <Text as="div" size="2" key={entry.dataKey} style={{ color: entry.color, textTransform: "capitalize" }}>
          {entry.dataKey}: {entry.value}%
        </Text>
      ))}
    </div>
  );
};

export function SentimentTrendChart({ data }) {
  const chartData = useMemo(() => prepareTrend(data), [data]);

  if (!chartData.length) {
    return <Flex align="center" justify="center" style={{ width: "100%", height: "100%" }}><Text size="2" color="gray">No sentiment data available for the selected period</Text></Flex>;
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={chartData} margin={{ top: 10, right: 16, bottom: 0, left: -16 }}>
        <CartesianGrid stroke="var(--gray-a4)" strokeDasharray="3 4" />
        <XAxis dataKey="label" axisLine={false} tickLine={false} minTickGap={32} tick={{ fill: "var(--gray-10)", fontSize: 11 }} />
        <YAxis domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} unit="%" axisLine={false} tickLine={false} tick={{ fill: "var(--gray-10)", fontSize: 11 }} />
        <Tooltip content={<TrendTooltip />} />
        <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, paddingTop: 8, textTransform: "capitalize" }} />
        <Line type="monotone" dataKey="positive" stroke="var(--green-9)" strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} isAnimationActive={false} />
        <Line type="monotone" dataKey="neutral" stroke="var(--orange-9)" strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} isAnimationActive={false} />
        <Line type="monotone" dataKey="negative" stroke="var(--red-9)" strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} isAnimationActive={false} />
      </LineChart>
    </ResponsiveContainer>
  );
}
